import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TitleMain from "../../../components/Title/TitleMain";
import { useModal } from "../../../contexts/ModalContext";
import { useUser } from "../../../contexts/UserContext";
import AxiosConfig from "../../../services/AxiosConfig";
import { useBreadcrumb } from "../../../contexts/BreadcrumbContext";
import { generateBreadcrumbSegmentsAdmin } from "../../../services/BreadcrumbService";
import SeanceCreation from "../../../components/Admin/Seances/SeanceCreation";
import SeancesVue from "../../../components/Admin/Seances/SeancesVue";
import SeancesAllVue from "../../../components/Admin/Seances/SeancesAllVue";
import { useLoading } from "../../../contexts/LoadingContext";

function AdminSeancesAllPage() {
    const { user } = useUser();
    const { showModal, hideModal } = useModal();
    const [myApiData, setMyApiData] = useState(null);
    const { updateDynamicBreadcrumb } = useBreadcrumb();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode') || 'consultation';
    const utilisateur = searchParams.get('filter_utilisateur') || null;
    const coach = searchParams.get('filter_coach') || null;
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const { isLoading, setLoading } = useLoading();
    const [page, setPage] = useState(parseInt(queryParams.get("page")) || 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalSeances, setTotalSeances] = useState(null);
    const [sortBy, setSortBy] = useState([]);
    const prevSortBy = useRef(sortBy);

    const putActifOpenModal = (row) => {
        const text = row.actif ? "supprimer cette séance" : "activer cette séance";
        showModal('confirm', {
            children: `Voulez-vous vraiment ${text} ?`,
            onConfirm: async () => {
                try {
                    const response = await AxiosConfig.post(`/admin/entreprises/${row.entreprise.id}/contrats/${row.contrat.id}/activites/${row.activite.id}/seances/${row.id}/actif`, { actif: !row.actif });
                    if (response.data.success) {
                        setTimeout(async () => {
                            getAllSeances();
                        }, 500);
                    }
                    handleSuccess("Statut modifié avec succès");
                } catch (error) {
                    console.error('error', error);
                    handleError();
                }
            }
        });
    };

    const handleSubmitSeanceStatus = async (values, { setSubmitting }) => {
        try {
            const response = await AxiosConfig.post(`/admin/seances/${values.id}/status`, values);
            if (response.data.success) {
                handleSuccess(response.data.message);
                getAllSeances();
            } else {
                console.error('error', response.data);
                handleError("Erreur : " + response.data.message);
            }
        } catch (error) {
            console.error('error', error);
            handleError();
        }
    };

    const onSortChange = ({ sortBy }) => {
        if (JSON.stringify(sortBy) !== JSON.stringify(prevSortBy.current)) {
            prevSortBy.current = sortBy;
            setSortBy(sortBy);
        }
    };

    const getInitialSortBy = () => {
        const sortBy = [];
        queryParams.getAll('sort_by').forEach((id, index) => {
            sortBy.push({ id, desc: queryParams.getAll('sort_desc')[index] === 'true' });
        });
        return sortBy;
    };

    const seancesVue = (
        <SeancesVue
            seances={myApiData}
            putActifOpenModal={putActifOpenModal}
            handleSubmitSeanceStatus={handleSubmitSeanceStatus}
            isLoading={isLoading}
            setPage={setPage}
            page={page}
            totalPage={totalPages}
            totalSeances={totalSeances}
            serverSideSorting={true}
            onSortChange={onSortChange}
            initialSortBy={getInitialSortBy()}
        />
    );

    const modeOptions = {
        'consultation': seancesVue,
        'default': seancesVue
    }
    const ComponentToRender = modeOptions[mode] || modeOptions['default'];

    const handleSuccess = (children = "Note enregistrée avec succès") => {
        showModal('success', { children: children, timeout: 1500 });
    };
    const handleError = (children = "Une erreur est survenue") => {
        showModal('error', { children: children, timeout: 1500 });
    };

    const getAllSeances = useCallback(async () => {
        setLoading(true);

        const newQueryParams = new URLSearchParams(location.search);
        if (utilisateur) newQueryParams.set('utilisateur', utilisateur);
        if (coach) newQueryParams.set('coach', coach);

        const url = `/admin/seances_all?${newQueryParams.toString()}`;
        try {
            const response = await AxiosConfig.get(url);
            const parseData = response.data.mySeances;
            setMyApiData(parseData);
            setTotalPages(response.data.totalPages);
            setTotalSeances(response.data.totalSeances);
        } catch (error) {
            console.error('error', error);
        }
        setLoading(false);
    }, [utilisateur, coach, location.search, setLoading]);

    useEffect(() => {
        updateDynamicBreadcrumb(generateBreadcrumbSegmentsAdmin({}));
    }, []);

    useEffect(() => {
        let queryParamsChange = false;
        const newQueryParams = new URLSearchParams(location.search);

        if (page && mode === 'consultation') {
            newQueryParams.set('page', page);
            queryParamsChange = true;
        }

        if (sortBy.length > 0 && mode === 'consultation') {
            newQueryParams.delete('sort_by');
            newQueryParams.delete('sort_desc');
            sortBy.forEach(sort => {
                newQueryParams.append('sort_by', sort.id);
                newQueryParams.append('sort_desc', sort.desc);
            });
            queryParamsChange = true;
        }

        if (queryParamsChange) {
            navigate(`${location.pathname}?${newQueryParams.toString()}`);
        }
    }, [page, sortBy, mode, navigate, location.pathname]);

    useEffect(() => {
        getAllSeances();
    }, [location.search]);

    return (myApiData && ComponentToRender)
}

export default AdminSeancesAllPage;
